import { useMemo, useState } from 'react';
import Expander from './Expander';
import { blockerOf, stillBlocked } from '../blocked';
import { useApp } from '../store';
import { displayName } from '../people';
import type { Issue, IssueStatus } from '../types';
import { PRIORITY_ORDER, STATUS_LABELS } from '../types';
import { statusOrderFor } from '../lifecycle';
import { relativeTime } from '../utils';
import Icon from './Icon';
import IssueText from './IssueText';
import Priority from './Priority';
import { useSampleBoard } from './SampleStrip';
import { can } from '../permissions';
import { hidesDone, inView, passes, viewsFor } from '../views';
import { livePhases } from '../phases';
import { targetPushes } from '../card-slip';
import { BacklogFilters } from './BacklogFilters';

const NO_EPIC = 'Unsorted';

export default function Backlog() {
  const issues = useApp((s) => s.issues);
  const search = useApp((s) => s.search);
  const focus = useApp((s) => s.focus);
  const focusWhy = useApp((s) => s.focusWhy);
  const clearFocus = useApp((s) => s.clearFocus);
  const groupBy = useApp((s) => s.groupBy);
  const setGroupBy = useApp((s) => s.setGroupBy);
  const openIssue = useApp((s) => s.openIssue);
  const moveIssue = useApp((s) => s.moveIssue);
  const sample = useSampleBoard();
  const canMove = useApp((s) => can(s.role, 'moveIssue')) && !sample.readOnly;
  // The In Staging status appears only on a board that turned the column on.
  const stagingOn = useApp((s) => s.meta.stagingEnabled === true);
  const filters = useApp((s) => s.filters);
  const areas = useApp((s) => s.areas);
  const issueTypes = useApp((s) => s.issueTypes);
  const phases = useApp((s) => s.phases);
  const labels = useApp((s) => s.labels);
  /* Retired areas still resolve — a card keeps what it was given. */
  const areaOf = (i: { area?: string | null }) => areas.find((a) => a.id === i.area);
  /* And retired phases too, for the same reason — the card keeps its stage. */
  const phaseOf = (i: { phase?: string | null }) => phases.find((p) => p.id === i.phase);
  const setFilters = useApp((s) => s.setFilters);
  const me = useApp((s) => s.user?.email ?? '');
  const profiles = useApp((s) => s.profiles);
  const projectId = useApp((s) => s.projectId);
  const savedViews = useApp((s) => s.savedViews);
  const viewId = useApp((s) => s.viewId);
  const setViewId = useApp((s) => s.setViewId);
  const showCounts = useApp((s) => s.showCounts);
  const setShowCounts = useApp((s) => s.setShowCounts);
  const saveView = useApp((s) => s.saveView);
  const forgetView = useApp((s) => s.forgetView);

  /*
   * The four the board comes with, then this person's own, narrowed to this
   * board — a view naming an area has nothing to say on a board without it.
   */
  const views = useMemo(() => viewsFor(savedViews, projectId), [savedViews, projectId]);
  const nameOf = (email: string) => displayName(email, profiles);
  /*
   * WHO CAN BE FILTERED BY: the people who actually hold work here.
   *
   * Not the membership list. A board of forty members where six have cards
   * would put thirty-four names in the menu, every one of them counting zero —
   * a longer list that answers fewer questions. Anyone who is given a card
   * appears the moment they are.
   */
  const people = useMemo(
    () =>
      [...new Set(issues.map((i) => i.assignee).filter((e): e is string => !!e))].sort((a, b) =>
        displayName(a, profiles).localeCompare(displayName(b, profiles))
      ),
    [issues, profiles]
  );

  // Collapse is for folding a group you still want listed; the status filter is for
  // removing it from the screen entirely. Both, because they answer different needs.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapse = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });


  const q = search.trim().toLowerCase();
  const matchesSearch = (i: Issue) =>
    !q ||
    i.title.toLowerCase().includes(q) ||
    i.ref.toLowerCase().includes(q) ||
    (i.epic ?? '').toLowerCase().includes(q) ||
    i.labels.some((l) => l.toLowerCase().includes(q));

  /**
   * Arrived from Next up asking for a named set of issues.
   *
   * It overrides the search and the filters rather than combining with them: the
   * point of following that link is to see those issues, and a filter left on
   * from an earlier visit quietly removing half of them would be worse than
   * useless — the reader would believe the number on the board was wrong.
   */
  /*
   * ── THE VIEW FIRST, THEN THE FILTERS — #301 ─────────────────────────────
   *
   * A view answers a question ("what is open", "what is mine"); the filters
   * narrow whatever it left. Two steps rather than one so the count line can
   * say WHICH of them is holding something back, which is the whole complaint:
   * work went missing and nothing on screen accounted for it.
   */
  const view = views.find((v) => v.id === viewId) ?? views[0];
  const inThisView = useMemo(() => inView(issues, view, me, NO_EPIC), [issues, view, me]);

  const visible = focus
    ? issues.filter((i) => focus.includes(i.id))
    : inThisView.filter((i) => matchesSearch(i) && passes(i, view?.filters ?? filters, NO_EPIC));

  // Meaningless while a focus is on: nothing is hidden by a filter then,
  // it is hidden because you asked for a named set.
  const hiddenByFilter = focus ? 0 : inThisView.filter(matchesSearch).length - visible.length;

  /**
   * ── HOW THE ROWS ARE DIVIDED — four ways, and one of them is "don't" ──────
   *
   * Grouping HIDES NOTHING. Every issue that passed the view and the filters
   * appears under exactly one heading whichever of these is chosen, which is
   * why Group by sits in Display and not in Filter.
   *
   * ⚠️ EVERY GROUPING NEEDS A HEADING FOR THE ONES THAT HAVE NO VALUE. On this
   * board 244 cards have no area and most have no epic — send those to no
   * group and the count line says 300 while 56 rows are drawn, which is the
   * complaint this card was raised about, arriving by a different door.
   */
  let groups: { key: string; label: string; dot?: IssueStatus; rows: Issue[] }[];
  if (groupBy === 'none') {
    /* One heading, so the group machinery below still has something to draw —
       and the count on it is the honest total, not a decoration. */
    groups = [{ key: 'all', label: 'All issues', rows: visible }];
  } else if (groupBy === 'status') {
    groups = statusOrderFor({ stagingEnabled: stagingOn }).map((s) => ({
      key: s,
      label: STATUS_LABELS[s],
      dot: s,
      rows: visible.filter((i) => i.status === s),
    }));
  } else if (groupBy === 'phase') {
    /*
     * BY STAGE, IN THE ORDER THE PLAN RUNS THEM — #377, WS-4. Unlike area and
     * epic below, a phase has a real order (its position), so this keeps it
     * rather than sorting busiest-first: reading the backlog top to bottom then
     * walks the project the way the Plan does. Grouped by phase id, not name, so
     * a retired stage that shares a name with a live one after a rename does not
     * collapse into it. Empty stages draw no heading; the "No phase" bucket goes
     * last and also catches any card whose phase is not one this project knows.
     */
    const known = new Set(phases.map((p) => p.id));
    const ordered = [...livePhases(phases), ...phases.filter((p) => p.retiredAt)];
    groups = [];
    for (const p of ordered) {
      const rows = visible.filter((i) => i.phase === p.id);
      if (rows.length) {
        groups.push({ key: p.id, label: p.retiredAt ? `${p.name} (retired)` : p.name, rows });
      }
    }
    const none = visible.filter((i) => !i.phase || !known.has(i.phase));
    if (none.length) groups.push({ key: '(no phase)', label: 'No phase yet', rows: none });
  } else {
    /* Epic and area are the same shape: a name off the card, a bucket for the
       cards without one, and the busiest first. Written once. */
    const NONE = groupBy === 'area' ? 'No area yet' : NO_EPIC;
    const keyOf = (i: Issue) =>
      groupBy === 'area' ? (areaOf(i)?.name ?? NONE) : i.epic || NONE;
    const names = [...new Set(visible.map(keyOf))];
    const openCount = (name: string) =>
      visible.filter(
        (i) => keyOf(i) === name && i.status !== 'done' && i.status !== 'wontdo'
      ).length;
    /* Busiest first, and the no-value bucket last however big it is: it is not
       a subject, it is the absence of one, and reading it at the top of the
       backlog tells you nothing about the work. */
    names.sort(
      (a, b) =>
        Number(a === NONE) - Number(b === NONE) ||
        openCount(b) - openCount(a) ||
        a.localeCompare(b)
    );
    groups = names.map((name) => ({
      key: name,
      label: name,
      rows: visible.filter((i) => keyOf(i) === name),
    }));
  }

  const sortRows = (rows: Issue[]) =>
    [...rows].sort((a, b) => {
      if (groupBy === 'epic' || groupBy === 'area' || groupBy === 'phase') {
        const openA = a.status !== 'done' && a.status !== 'wontdo' ? 0 : 1;
        const openB = b.status !== 'done' && b.status !== 'wontdo' ? 0 : 1;
        if (openA !== openB) return openA - openB;
      }
      if (filters.sort === 'priority') {
        const p = PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority);
        if (p !== 0) return p;
      }
      if (filters.sort === 'pushed') {
        // Most-pushed target dates first, on live cards only (a date moved before
        // work starts is re-planning, not slippage). Ties fall through to recency.
        const p = targetPushes(b) - targetPushes(a);
        if (p !== 0) return p;
      }
      if (filters.sort === 'area') {
        // By the area's name, A→Z; a card with no area sorts to the end rather
        // than jumping to the top on an empty string. Ties fall through to recency.
        const an = areaOf(a)?.name ?? '￿';
        const bn = areaOf(b)?.name ?? '￿';
        const c = an.localeCompare(bn);
        if (c !== 0) return c;
      }
      return filters.sort === 'oldest' ? a.updatedAt - b.updatedAt : b.updatedAt - a.updatedAt;
    });

  return (
    <div className="backlog">
      {/* Says what you are looking at and how to leave. A screen silently
          showing three of a hundred rows, with the filters above it reading as
          though nothing were applied, is a screen nobody can trust. */}
      {focus && (
        <div className="focus-bar">
          <span>
            Showing the {focus.length} card{focus.length === 1 ? '' : 's'}
            {focusWhy ? ` ${focusWhy}` : ''}.
          </span>
          <button className="link" onClick={clearFocus}>
            Show the whole backlog
          </button>
        </div>
      )}

      <BacklogFilters
        issues={issues}
        inThisView={inThisView}
        areas={areas}
        types={issueTypes}
        phases={phases}
        labels={labels}
        people={people}
        nameOf={nameOf}
        noEpic={NO_EPIC}
        filters={filters}
        onFilters={(f) => setFilters(f)}
        views={views}
        viewId={viewId}
        onView={setViewId}
        countInView={(v) => inView(issues, v, me, NO_EPIC).length}
        groupBy={groupBy}
        onGroupBy={setGroupBy}
        showCounts={showCounts}
        onShowCounts={setShowCounts}
        onSaveView={saveView}
        onForgetView={forgetView}
        shown={visible.length}
        hiddenByFilters={hiddenByFilter}
        hidesDone={hidesDone(view)}
      />

      {visible.length === 0 && (
        <div className="bl-empty">
          {q ? `No issues match “${search}”.` : 'Nothing matches these filters.'}
        </div>
      )}

      {groups.map((g) => {
        if (g.rows.length === 0) return null;
        const open = g.rows.filter((i) => i.status !== 'done' && i.status !== 'wontdo').length;
        const isShut = collapsed.has(g.key);
        return (
          <section key={g.key} className="bl-group">
            <button
              className={`bl-group-head ${isShut ? 'shut' : ''}`}
              onClick={() => toggleCollapse(g.key)}
              aria-expanded={!isShut}
            >
              <span className="bl-chev">
                <Icon name="chevron" size={18} />
              </span>
              {g.dot && <span className={`status-dot dot-${g.dot}`} />}
              <h2>{g.label}</h2>
              {/* Off by choice, in Display. Somebody grouping by area to read
                  the names does not always want a number beside each. */}
              {showCounts && <span className="bl-count">{g.rows.length}</span>}
              {showCounts && groupBy !== 'status' && open > 0 && (
                <span className="bl-open">{open} open</span>
              )}
            </button>

            <Expander open={!isShut}>
              <div className="bl-rows">
                {sortRows(g.rows).map((i) => (
                  <article
                    key={i.id}
                    className={`bl-row bl-row-${i.status}`}
                    onClick={() => openIssue(i.id)}
                  >
                    <div className="bl-main">
                      <div className="bl-titleline">
                        <span className="bl-ref">{i.ref}</span>
                        {stillBlocked(i, issues) && (
                          <span className="flag-loz">
                            <Icon name="flag" size={11} />
                            Blocked
                          </span>
                        )}
                        <span className="bl-title">{i.title}</span>
                      </div>

                      {stillBlocked(i, issues) && (
                        // A div, not a <p>: IssueText renders its own <p>/<ul> blocks,
                        // and a <p> inside a <p> is invalid (the browser silently closes
                        // the outer one). .flag-reason styles the same either way.
                        <div className="flag-reason">
                          <IssueText text={blockerOf(i)} />
                        </div>
                      )}
                    </div>

                    <div className="bl-meta">
                      {/*
                        * THE AREA AS A CHIP, in the same colour as the rail on
                        * the board card. Vinnie, 26 August 2026: "they can go
                        * back to the backlog and see what that area is on a
                        * chip in the same colour." One colour, two shapes — the
                        * rail is learned here and read there.
                        */}
                      {areaOf(i) && (
                        <span className="area-chip">
                          <span className="area-dot" style={{ background: areaOf(i)!.colour }} />
                          <span className="nm">{areaOf(i)!.name}</span>
                          {areaOf(i)!.retiredAt ? ' (retired)' : ''}
                        </span>
                      )}
                      {/* The stage, the same round-dot chip the card face and the
                          open card draw — so the three tell one story. Dropped
                          when the rows are already grouped by phase, where the
                          heading says it. */}
                      {groupBy !== 'phase' && phaseOf(i) && (
                        <span className="phase-chip">
                          <span className={`phase-chip-dot phase-${phaseOf(i)!.status}`} />
                          <span className="nm">{phaseOf(i)!.name}</span>
                          {phaseOf(i)!.retiredAt ? ' (retired)' : ''}
                        </span>
                      )}
                      {groupBy !== 'epic' && i.epic && <span className="epic-chip">{i.epic}</span>}
                      {/* The shared Priority mark, not a colour dot — the same
                          chevron-and-word the board card and modal use, so
                          priority reads identically wherever it appears. The
                          backlog was the one list still drawing its own dot,
                          which is exactly the odd-one-out the Priority
                          component exists to prevent. */}
                      <Priority priority={i.priority} />
                      {/* The slippage token — how many times this card's target date
                          has been pushed later, shown only on live cards (migration
                          0125, targetPushes is live-gated). The "Most pushed" sort
                          brings these to the top; the token is why they are there. */}
                      {targetPushes(i) > 0 && (
                        <span className="bl-push" title={`Due date pushed later ${targetPushes(i)} time${targetPushes(i) === 1 ? '' : 's'}`}>
                          moved {targetPushes(i)}×
                        </span>
                      )}
                      <span className="bl-time">{relativeTime(i.updatedAt)}</span>
                      {/*
                        * ── MOVING A CARD FROM THE BACKLOG, IN EVERY GROUPING ──
                        *
                        * This read `groupBy === 'status' ? null : …`, so the one
                        * control that CHANGES anything was missing from the
                        * default view of the screen. The reasoning was that the
                        * heading above already says the status, so the dropdown
                        * repeats it — true, and beside the point: it is not
                        * there to report the status, it is there to change it.
                        *
                        * Vinnie, 31 August 2026: *"that was actually a nice
                        * feature to do from the backlog."* It predates #301 and
                        * is on live in the same state; he noticed it because
                        * #301 gave him a reason to look at this row.
                        *
                        * Grouped by status the row now jumps to another group
                        * as you change it, which is the confirmation you want
                        * rather than a reason to hide the control.
                        */}
                      {canMove ? (
                        <select
                          className="bl-status"
                          value={i.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => moveIssue(i.id, e.target.value as IssueStatus)}
                        >
                          {statusOrderFor({ stagingEnabled: stagingOn || i.status === 'staging' }).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="bl-status-read">
                          <span className={`status-dot dot-${i.status}`} /> {STATUS_LABELS[i.status]}
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </Expander>
          </section>
        );
      })}
    </div>
  );
}
